<script setup>
import { computed, useTemplateRef } from 'vue';
import { useStore } from 'vuex';
import { __, __x, __nx } from 'JIX/gettext.js';
import DsPagination from 'JIX/components/ds/DsPagination.vue';
import CompanyCard from 'JIX/components/CompanyCard.vue';
import DsBreadcrumb from 'JIX/components/ds/DsBreadcrumb.vue';
import RenderTranslation from 'JIX/components/RenderTranslation.vue';
import { mapParam } from 'JIX/jobsearch/utils';

const store = useStore();

const header = useTemplateRef('header');
const sort = mapParam(store, 'sort');

const breadCrumbItems = [
    [__('Søgeresultat'), showListMode],
    [__('Virksomheder')],
];

const page = computed({
    get: () => store.state.searchQuery.page || 1,
    set: async (val) => {
        await store.dispatch('goToPage', val);
        header.value.scrollIntoView({ behavior: 'smooth' });
    },
});

/**
 * @typedef {Object} CompanyUnsolicitedData
 * @property {string} companyId
 * @property {string} companyName
 * @property {string} companyProfileHtml
 * @property {boolean} disableRating
 * @property {number} hitcount
 * @property {boolean} isFollowing
 * @property {string=} logoUrl
 * @property {string} profileEvaluationsUrl
 * @property {string} profileUrl
 * @property {number=} ratingRoundedUp
 * @property {number=} ratingsCount
 * @property {string} jobsearchUrl
 */
/**
 * @type {import('vue').ComputedRef<CompanyUnsolicitedData[]>}
 */
const unsolicitedData = computed(() => store.state.searchResponse.companies);

function handleUpdateFollow(following, index) {
    store.commit('updateIsFollowingCompany', { following, index });
}

async function showListMode(){
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return store.dispatch('performSearch', { newViewMode: 'list' });
}

function handleUpdateSort(event) {
    sort.value = event.target.value;
    store.dispatch('performSearch');
}

</script>

<template>
    <div class="container mb-5">
        <DsBreadcrumb class="mb-4 ml-lg-5" :items="breadCrumbItems" />
        <div class="row">
            <div class="col-12 col-md-10 col-lg-8 mx-auto">
                <div v-if="store.state.searchResponse.hitcount > 0">
                    <h1 ref="header">
                        {{ __("Vil du søge uopfordret?") }}
                    </h1>
                    <p>
                        {{ __("Vi har nedenfor samlet en liste af virksomheder, der tidligere har søgt medarbejdere, der matcher din søgning. Da de tidligere har efterspurgt dine kompetencer, kan du måske søge uopfordret hos dem? Se også vores ") }}
                        <a :href="store.state.unsolicitedGuideUrl">
                            {{ __("guide til ansøgninger") }}
                        </a>
                    </p>
                    <div class="d-flex justify-content-between align-items-end mb-3">
                        <div class="flex-grow-1 text-muted text-sm">
                            <RenderTranslation
                                :message="{
                                    translation: __x('<strong:{pageStart}> til <strong:{pageEnd}> af <strong:{hitcount}> resultater.',{
                                        pageStart: (page - 1) * store.state.searchResponse.page_size + 1,
                                        pageEnd: Math.min(page * store.state.searchResponse.page_size, store.state.searchResponse.hitcount),
                                        hitcount: store.state.searchResponse.hitcount,
                                    }),
                                }"
                            />
                        </div>
                        <div v-if="store.state.companySortOptions.length">
                            <select class="form-control" :value="sort" @change="handleUpdateSort">
                                <option
                                    v-for="([text, value]) in store.state.companySortOptions"
                                    :value="value"
                                    :key="text + value"
                                >
                                    {{ text }}
                                </option>
                            </select>
                        </div>
                    </div>
                    <CompanyCard
                        class="mb-4"
                        v-for="company, index in unsolicitedData"
                        :show-follow-button="store.state.searchResponse.show_follow"
                        @update-follow-status="res => handleUpdateFollow(res.following, index)"
                        :company="company"
                        :key="company.companyId"
                    />
                    <div class="d-flex justify-content-center">
                        <DsPagination
                            class="mt-4"
                            :items="store.state.searchResponse.hitcount"
                            :per-page="store.state.searchResponse.page_size"
                            :base-url="store.state.searchResponse.link_canonical"
                            v-model:page="page"
                            hide-last-page
                        />
                    </div>
                </div>
                <div v-else>
                    <h2>{{ __('0 virksomheder') }}</h2>
                    <div class="rounded bg-surface-1 border p-3">
                        <h5>{{ __('Ingen resultater') }}</h5>
                        <p>
                            {{ __('Der er ingen virksomheder, der matcher din søgning.') }}
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    class="btn btn-secondary btn-sm mt-4"
                    @click.prevent="showListMode"
                >
                    {{ __('Tilbage til jobannoncer') }}
                </button>
            </div>
        </div>
    </div>
</template>
